import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, GraduationCap, Quote } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import DoctorPortrait, { CredentialList } from "@/components/DoctorPortrait";
import { Reveal, Eyebrow, SectionTitle } from "@/components/Reveal";
import { CLINIC, physicianJsonLd } from "@/data/content";

const About = () => (
  <>
    <SEO
      title="About Dr. Ankur Lal | STD & Sexual Health Specialist, Delhi"
      description="Dr. Ankur Lal — STD & Sexual Health Specialist | Dermatologist. Ex-STD Specialist & Former Senior Resident, AIIMS New Delhi. Gold Medalist, MD Dermatology. MBBS, King George's Medical University, Lucknow."
      path="/about-dr-ankur-lal"
      jsonLd={physicianJsonLd}
    />
    <PageHero
      eyebrow="About Your Doctor"
      title="Dr. Ankur Lal"
      intro="STD & Sexual Health Specialist | Dermatologist — providing confidential, evidence-based sexual-health care in Delhi, online and in clinic."
    />

    <section className="py-16 sm:py-24" data-testid="about-profile">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <DoctorPortrait />
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>Profile</Eyebrow>
            <SectionTitle className="mt-4">Meet Dr. Ankur Lal</SectionTitle>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Dr. Ankur Lal is a Dermatologist and STD & Sexual Health Specialist with
              clinical experience in the diagnosis and management of sexually transmitted
              infections and sexual-health conditions. He is a Gold Medalist in MD
              Dermatology and has previously worked as an STD Specialist and Senior
              Resident at AIIMS New Delhi.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              His practice is built around one principle: sexual health is healthcare —
              and every patient deserves expert, private and judgment-free medical care.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-9">
            <h3 className="text-xs font-semibold tracking-[0.25em] uppercase text-emerald-900/80 mb-5">
              Credentials
            </h3>
            <CredentialList />
          </Reveal>

          <Reveal delay={0.15} className="mt-9">
            <div className="bg-secondary border border-amber-600/20 rounded-3xl p-7">
              <Quote className="w-6 h-6 text-amber-700" />
              <p className="mt-3 font-serif text-xl sm:text-2xl text-slate-900 leading-relaxed">
                "Many people delay seeking help for sexual-health concerns because of
                embarrassment. My role is to make that first step easy — private,
                respectful and completely professional."
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link
              to="/booking?type=online"
              data-testid="about-consult-cta"
              className="inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all shadow-md hover:-translate-y-0.5"
            >
              <CalendarCheck className="w-4 h-4" /> Consult Dr. Ankur Lal
            </Link>
            <Link
              to="/std-sti-treatment"
              data-testid="about-services-link"
              className="inline-flex items-center justify-center gap-2 text-emerald-950 font-semibold text-sm px-5 py-3.5 underline underline-offset-8 decoration-emerald-700/40"
            >
              View treatments <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-16 sm:py-24 bg-white border-y border-slate-900/8" data-testid="about-education">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Education & Training</Eyebrow>
          <SectionTitle className="mt-4">Medical Education</SectionTitle>
        </Reveal>
        <div className="mt-10 space-y-5">
          {[
            { degree: "MBBS", institute: "King George's Medical University, Lucknow" },
            { degree: "MD Dermatology (Gold Medalist)", institute: "VMMC & Safdarjung Hospital, New Delhi" },
            { degree: "STD Specialist & Senior Resident", institute: "AIIMS New Delhi (former)" },
          ].map((e, i) => (
            <Reveal key={e.degree} delay={i * 0.08}>
              <div className="flex items-start gap-5 bg-[#FAF9F6] border border-slate-900/10 rounded-3xl p-6">
                <span className="w-11 h-11 rounded-2xl bg-emerald-900 flex items-center justify-center shrink-0">
                  <GraduationCap className="w-5 h-5 text-emerald-100" />
                </span>
                <div>
                  <p className="font-serif text-xl font-semibold text-slate-950">{e.degree}</p>
                  <p className="text-sm text-slate-600 mt-1">{e.institute}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBand
      title="Consult Dr. Ankur Lal — Confidentially"
      text="Online and clinic consultations available for STD/STI, HIV and sexual-health concerns."
    />
  </>
);

export default About;
